<template>
  <div class="min-h-screen bg-gray-50 flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8" dir="rtl">
    <div class="max-w-md w-full space-y-8">
      <!-- Header -->
      <div class="text-center">
        <div class="mx-auto h-12 w-12 flex items-center justify-center rounded-full bg-blue-100">
          <FileText class="h-6 w-6 text-blue-600" />
        </div>
        <h2 class="mt-6 text-3xl font-bold text-gray-900">Welcome Back</h2>
        <p class="mt-2 text-sm text-gray-600">
          Sign in to your account
        </p>
      </div>

      <!-- Error Message -->
      <div v-if="error" class="bg-red-50 border border-red-200 rounded-md p-4">
        <div class="flex">
          <X class="h-5 w-5 text-red-400 ml-2" />
          <div class="text-sm text-red-800">{{ error }}</div>
        </div>
      </div>

      <!-- Success Message -->
      <div v-if="successMessage" class="bg-green-50 border border-green-200 rounded-md p-4">
        <div class="flex">
          <Check class="h-5 w-5 text-green-400 ml-2" />
          <div class="text-sm text-green-800">{{ successMessage }}</div>
        </div>
      </div>

      <!-- Login Form -->
      <form class="mt-8 space-y-6" @submit.prevent="handleLogin">
        <div class="space-y-4">
          <!-- Email -->
          <div>
            <label for="email" class="block text-sm font-medium text-gray-700 text-right">
              Email Address
            </label>
            <div class="mt-1">
              <input
                id="email"
                v-model="form.email"
                name="email"
                type="email"
                autocomplete="email"
                required
                class="appearance-none relative block w-full px-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500 focus:z-10 sm:text-sm text-right"
                placeholder="Enter your email"
                :disabled="isLoading"
              />
            </div>
          </div>

          <!-- Password -->
          <div>
            <label for="password" class="block text-sm font-medium text-gray-700 text-right">
              Password
            </label>
            <div class="mt-1">
              <input
                id="password"
                v-model="form.password"
                name="password"
                :type="showPassword ? 'text' : 'password'"
                autocomplete="current-password"
                required
                class="appearance-none relative block w-full px-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500 focus:z-10 sm:text-sm text-right"
                placeholder="Enter your password"
                :disabled="isLoading"
              />
            </div>
          </div>
        </div>

        <!-- Remember me & Forgot password -->
        <div class="flex items-center justify-between">
          <div class="flex items-center">
            <input
              id="remember-me"
              v-model="rememberMe"
              name="remember-me"
              type="checkbox"
              class="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
            />
            <label for="remember-me" class="mr-2 block text-sm text-gray-900">
              Remember me
            </label>
          </div>

          <div class="text-sm">
            <router-link
              to="/forgot-password"
              class="font-medium text-blue-600 hover:text-blue-500"
            >
              Forgot your password?
            </router-link>
          </div>
        </div>

        <!-- Submit Button -->
        <div>
          <button
            type="submit"
            :disabled="isLoading"
            class="group relative w-full flex justify-center py-2 px-4 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <span v-if="isLoading" class="flex items-center">
              <div class="animate-spin rounded-full h-4 w-4 border-b-2 border-white ml-2"></div>
              Signing in...
            </span>
            <span v-else>Sign in</span>
          </button>
        </div>

        <!-- Register Link -->
        <div class="text-center">
          <span class="text-sm text-gray-600">
            Don't have an account? 
          </span>
          <router-link
            to="/register"
            class="font-medium text-blue-600 hover:text-blue-500 text-sm"
          >
            Sign up now
          </router-link>
        </div>
      </form>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, reactive, computed } from 'vue';
import { useRouter } from 'vue-router';
import { useAuthStore } from '../stores/auth';
import { FileText, X, Check } from 'lucide-vue-next';
import type { LoginCredentials } from '../types/auth';

const router = useRouter();
const authStore = useAuthStore();

// Form state
const form = reactive<LoginCredentials>({
  email: '',
  password: '',
});

const rememberMe = ref(false);
const showPassword = ref(false);
const error = ref<string | null>(null);
const successMessage = ref<string | null>(null);

// Computed
const isLoading = computed(() => authStore.isLoading);

// Methods
const handleLogin = async () => {
  if (!form.email || !form.password) {
    error.value = 'Please fill in all fields';
    return;
  }

  error.value = null;
  successMessage.value = null;

  try {
    const response = await authStore.login(form);
    
    console.log('Login response:', response);
    console.log('Auth store user after login:', authStore.user);
    console.log('Auth store token after login:', authStore.token);
    console.log('Is authenticated:', authStore.isAuthenticated);
    
    if (response.success) {
      successMessage.value = 'Login successful! Redirecting...';
      
      // Redirect to main application immediately
      console.log('Attempting to redirect to /');
      router.push('/');
    } else {
      error.value = response.error || 'Login failed';
    }
  } catch (err: any) {
    console.error('Login error:', err);
    error.value = err.message || 'An unexpected error occurred';
  }
};
</script>

<style scoped>
.animate-spin {
  animation: spin 1s linear infinite;
}

@keyframes spin {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}

/* Hide app navigation on login page */
:deep(#app > nav) {
  display: none;
}
</style>
